import type { Ref } from '@vue/composition-api';
import { computed, ref } from '@vue/composition-api';
import { defineStore } from 'pinia';
import { DocumentRevisionStatus } from '@getaccept/lib-shared/src/types/document-revision';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import { ChannelType, PusherEvent } from '@getaccept/pusher';
import { DsrService } from '../../api/dsr/service/dsr.service';
import { pusherClient } from '../../api/pusher/pusher-client';

export const useRevisionsStore = defineStore('revisions', () => {
  const revision: Ref<number> = ref(null);
  const revisions: Ref<DocumentRecipientRevision[]> = ref([]);
  const showReviewChanges = ref(false);
  const loadingRevisions = ref(false);

  const loadRevisions = async () => {
    loadingRevisions.value = true;

    try {
      revisions.value = await DsrService.loadRevisions();
    } catch (e) {
      loadingRevisions.value = false;
      revisions.value = [];
    } finally {
      loadingRevisions.value = false;
    }
  };

  const setRevision = (newRevision: number) => {
    revision.value = newRevision;
  };

  const setRevisions = (payload: DocumentRecipientRevision[]) => {
    revisions.value = payload;
  };

  const setViewedLoading = ref(false);

  const setRevisionViewed = async (revision: number) => {
    setViewedLoading.value = true;

    try {
      await DsrService.addRecipientView(revision);
    } catch (e) {
      setViewedLoading.value = false;
      console.error(e);
    } finally {
      setViewedLoading.value = false;
    }
  };

  const revisionsLoading = computed(() => loadingRevisions.value || setViewedLoading.value);

  const latestSealedRevision = computed(
    () =>
      revisions.value
        .filter(revision => revision.status === DocumentRevisionStatus.Sealed)
        .reverse()[0]
  );

  const listenToNewRevisions = async (dsrId: string) => {
    const documentPusherChannel = await pusherClient.subscribe(ChannelType.Document, dsrId);

    pusherClient.listen(
      documentPusherChannel,
      PusherEvent.Published,
      (event: { documentId: string; entityId: string; revision: number }) => {
        if (event) {
          handleNewRevisionPushed(20000);
        }
      }
    );
  };

  const showRevisionPromptBar = ref(false);
  const waitingToShowReviewModal = ref(false);

  const handleNewRevisionPushed = (timerDuration: number) => {
    showRevisionPromptBar.value = true;
    startForcedReviewTimer(timerDuration);
    waitingToShowReviewModal.value = true;
    setTimeout(() => {
      if (waitingToShowReviewModal.value) {
        showReviewChangesModal();
      }
    }, timerDuration);
  };

  const forcedReviewTimer = ref(20);

  const startForcedReviewTimer = (duration: number) => {
    let timeLeft = duration / 1000;
    const timer = setInterval(() => {
      if (timeLeft === 0) {
        clearInterval(timer);
      } else {
        timeLeft -= 1;
      }
      forcedReviewTimer.value = timeLeft;
    }, 1000);
  };

  const loadNewContent = ref(false);

  const showReviewChangesModal = async () => {
    waitingToShowReviewModal.value = false;
    await loadRevisions();

    if (latestSealedRevision.value?.message) {
      showRevisionPromptBar.value = false;
      showReviewChanges.value = true;
      return;
    }

    loadNewContent.value = true;
    showRevisionPromptBar.value = false;
  };

  const handleReviewChanges = () => {
    loadNewContent.value = true;
    showReviewChanges.value = false;
  };

  return {
    setRevisionViewed,
    loadRevisions,
    setRevisions,
    latestSealedRevision,
    revisionsLoading,
    showReviewChanges,
    listenToNewRevisions,
    handleReviewChanges,
    showReviewChangesModal,
    showRevisionPromptBar,
    forcedReviewTimer,
    loadNewContent,
    revisions,
    setRevision,
    revision,
  };
});
